// src/inventory/controllers/inventoryController.js
// ─────────────────────────────────────────────────────────────────────────────
import apiService from '../services/apiService';
import {
  Category, SubCategory, Brand, Unit, Product,
  Warehouse, Location, Supplier,
  PurchaseOrder, PurchaseReturn,
  StockIn, StockOut, StockTransfer, StockAdjustment,
} from '../models/inventoryModels';

const unwrap = (res) => {
  if (!res) return {};
  if ('success' in res) return res;
  if (res.data && 'success' in res.data) return res.data;
  return res;
};

const buildQP = (f = {}) =>
  new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([,v]) => v !== '' && v != null))).toString();

// Generic CRUD factory
const makeCRUD = (endpoint, ModelClass) => ({
  getAll: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(qp ? `${endpoint}?${qp}` : endpoint));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(d => ModelClass.fromJSON(d)), total: raw.total || list.length, totalPages: raw.totalPages || 1, page: raw.page || 1 };
    } catch (err) {
      return { success: false, data: [], total: 0, message: err?.response?.data?.message || err.message };
    }
  },
  getById: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`${endpoint}/${id}`));
      return { success: true, data: ModelClass.fromJSON(raw.data || raw) };
    } catch (err) {
      return { success: false, data: null, message: err?.response?.data?.message || err.message };
    }
  },
  create: async (data) => {
    try {
      const obj = new ModelClass(data);
      const v   = obj.validate();
      if (!v.isValid) return { success: false, errors: v.errors };
      const raw = unwrap(await apiService.post(endpoint, obj.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };
      return { success: true, data: ModelClass.fromJSON(raw.data || raw), message: raw.message || 'Created successfully' };
    } catch (err) {
      const e = err?.response?.data;
      return { success: false, message: e?.message || err.message, errors: e?.errors || [] };
    }
  },
  update: async (id, data) => {
    try {
      const obj = new ModelClass({ ...data, id });
      const v   = obj.validate();
      if (!v.isValid) return { success: false, errors: v.errors };
      const raw = unwrap(await apiService.put(`${endpoint}/${id}`, obj.toJSON()));
      if (raw.success === false) return { success: false, errors: raw.errors || [], message: raw.message };
      return { success: true, data: ModelClass.fromJSON(raw.data || raw), message: raw.message || 'Updated successfully' };
    } catch (err) {
      const e = err?.response?.data;
      return { success: false, message: e?.message || err.message, errors: e?.errors || [] };
    }
  },
  delete: async (id) => {
    try {
      const raw = unwrap(await apiService.delete(`${endpoint}/${id}`));
      return { success: true, message: raw.message || 'Deleted successfully' };
    } catch (err) {
      return { success: false, message: err?.response?.data?.message || err.message };
    }
  },
  getStats: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`${endpoint}/stats${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data || raw };
    } catch (err) {
      return { success: false, data: null, message: err.message };
    }
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// 1. CATEGORY
// ─────────────────────────────────────────────────────────────────────────────
export const categoryController = {
  ...makeCRUD('inventory/categories', Category),
  getList: async (companyId) => {
    try {
      const raw  = unwrap(await apiService.get(`inventory/categories/list${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. SUB CATEGORY
// ─────────────────────────────────────────────────────────────────────────────
export const subCategoryController = {
  ...makeCRUD('inventory/sub-categories', SubCategory),
  getByCategory: async (categoryId) => {
    try {
      const raw  = unwrap(await apiService.get(`inventory/sub-categories?categoryId=${categoryId}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data.map(d => SubCategory.fromJSON(d)) : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. BRAND
// ─────────────────────────────────────────────────────────────────────────────
export const brandController = {
  ...makeCRUD('inventory/brands', Brand),
  getList: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/brands/list${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. UNIT
// ─────────────────────────────────────────────────────────────────────────────
export const unitController = {
  ...makeCRUD('inventory/units', Unit),
  getList: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/units/list${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. PRODUCT
// ─────────────────────────────────────────────────────────────────────────────
export const productController = {
  ...makeCRUD('inventory/products', Product),
  search: async (query, companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/products/search?q=${encodeURIComponent(query)}${companyId ? `&companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data.map(d => Product.fromJSON(d)) : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getLowStock: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/products/low-stock${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data.map(d => Product.fromJSON(d)) : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getExpiring: async (days = 30, companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/products/expiring?days=${days}${companyId ? `&companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data.map(d => Product.fromJSON(d)) : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getLedger: async (productId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/products/${productId}/ledger`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  generateBarcode: async (productId) => {
    try {
      const raw = unwrap(await apiService.post(`inventory/products/${productId}/barcode`, {}));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) { return { success: false, message: err.message }; }
  },
  uploadImages: async (productId, files) => {
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      const raw = unwrap(await apiService.post(`inventory/products/${productId}/images`, fd));
      return { success: true, data: raw.data, message: raw.message };
    } catch (err) { return { success: false, message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. WAREHOUSE
// ─────────────────────────────────────────────────────────────────────────────
export const warehouseController = {
  ...makeCRUD('inventory/warehouses', Warehouse),
  getList: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/warehouses/list${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getStockSummary: async (warehouseId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/warehouses/${warehouseId}/stock-summary`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. STORAGE LOCATION
// ─────────────────────────────────────────────────────────────────────────────
export const locationController = {
  ...makeCRUD('inventory/locations', Location),
  getByWarehouse: async (warehouseId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/locations?warehouseId=${warehouseId}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data.map(d => Location.fromJSON(d)) : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. SUPPLIER
// ─────────────────────────────────────────────────────────────────────────────
export const supplierController = {
  ...makeCRUD('inventory/suppliers', Supplier),
  search: async (query) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/suppliers/search?q=${encodeURIComponent(query)}`));
      return { success: true, data: Array.isArray(raw.data) ? raw.data : [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getLedger: async (supplierId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/suppliers/${supplierId}/ledger`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. PURCHASE ORDER
// ─────────────────────────────────────────────────────────────────────────────
export const purchaseOrderController = {
  ...makeCRUD('inventory/purchase-orders', PurchaseOrder),
  approve: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/purchase-orders/${id}/approve`, {}));
      return { success: true, data: PurchaseOrder.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
  receive: async (id, receiveData) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/purchase-orders/${id}/receive`, receiveData));
      return { success: true, data: PurchaseOrder.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
  cancel: async (id, reason) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/purchase-orders/${id}/cancel`, { reason }));
      return { success: true, message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
  recordPayment: async (id, { amount, method, reference }) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/purchase-orders/${id}/payment`, { amount, method, reference }));
      return { success: true, data: PurchaseOrder.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 10. PURCHASE RETURN
// ─────────────────────────────────────────────────────────────────────────────
export const purchaseReturnController = {
  ...makeCRUD('inventory/purchase-returns', PurchaseReturn),
  approve: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/purchase-returns/${id}/approve`, {}));
      return { success: true, data: PurchaseReturn.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 11. STOCK IN
// ─────────────────────────────────────────────────────────────────────────────
export const stockInController = {
  ...makeCRUD('inventory/stock-in', StockIn),
};

// ─────────────────────────────────────────────────────────────────────────────
// 12. STOCK OUT
// ─────────────────────────────────────────────────────────────────────────────
export const stockOutController = {
  ...makeCRUD('inventory/stock-out', StockOut),
};

// ─────────────────────────────────────────────────────────────────────────────
// 13. STOCK TRANSFER
// ─────────────────────────────────────────────────────────────────────────────
export const stockTransferController = {
  ...makeCRUD('inventory/transfers', StockTransfer),
  approve: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/transfers/${id}/approve`, {}));
      return { success: true, data: StockTransfer.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
  dispatch: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/transfers/${id}/dispatch`, {}));
      return { success: true, data: StockTransfer.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
  receive: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/transfers/${id}/receive`, {}));
      return { success: true, data: StockTransfer.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 14. STOCK ADJUSTMENT
// ─────────────────────────────────────────────────────────────────────────────
export const stockAdjustmentController = {
  ...makeCRUD('inventory/adjustments', StockAdjustment),
  approve: async (id) => {
    try {
      const raw = unwrap(await apiService.patch(`inventory/adjustments/${id}/approve`, {}));
      return { success: true, data: StockAdjustment.fromJSON(raw.data || raw), message: raw.message };
    } catch (err) { return { success: false, message: err?.response?.data?.message || err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 15. STOCK MANAGEMENT (aggregated view)
// ─────────────────────────────────────────────────────────────────────────────
export const stockController = {
  getStockSummary: async (companyId, filters = {}) => {
    try {
      const qp  = buildQP({ companyId, ...filters });
      const raw = unwrap(await apiService.get(`inventory/stock/summary?${qp}`));
      return { success: true, data: raw.data || [], total: raw.total || 0 };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getStockMovements: async (productId, filters = {}) => {
    try {
      const qp  = buildQP({ productId, ...filters });
      const raw = unwrap(await apiService.get(`inventory/stock/movements?${qp}`));
      return { success: true, data: raw.data || [], total: raw.total || 0 };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getDashboard: async (companyId) => {
    try {
      const raw = unwrap(await apiService.get(`inventory/stock/dashboard${companyId ? `?companyId=${companyId}` : ''}`));
      return { success: true, data: raw.data };
    } catch (err) { return { success: false, data: null, message: err.message }; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// 16. REPORTS
// ─────────────────────────────────────────────────────────────────────────────
export const reportController = {
  getStockReport: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(`inventory/reports/stock?${qp}`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getMovementReport: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(`inventory/reports/movements?${qp}`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getPurchaseReport: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(`inventory/reports/purchase?${qp}`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  getValuationReport: async (filters = {}) => {
    try {
      const qp  = buildQP(filters);
      const raw = unwrap(await apiService.get(`inventory/reports/valuation?${qp}`));
      return { success: true, data: raw.data || [] };
    } catch (err) { return { success: false, data: [], message: err.message }; }
  },
  export: async (type, filters = {}) => {
    try {
      const qp  = buildQP({ ...filters, format: 'csv' });
      const res = await apiService.get(`inventory/reports/${type}/export?${qp}`, { responseType: 'blob' });
      return { success: true, data: res.data || res };
    } catch (err) { return { success: false, message: err.message }; }
  },
};

export default {
  categoryController, subCategoryController, brandController, unitController,
  productController, warehouseController, locationController, supplierController,
  purchaseOrderController, purchaseReturnController,
  stockInController, stockOutController, stockTransferController, stockAdjustmentController,
  stockController, reportController,
};